/**
 * SphereTimeline - Main timeline organism with infinite scroll
 * Displays all lifecycle events for a sphere from Cosmos DB
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Timeline } from 'primereact/timeline';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useTranslation } from 'localization';
import type { ApiClients } from '../../../context/ApiClientsContext';
import {
  TimelineEvent,
  TimelineQueryResponse,
  TimelineFilters as TimelineFiltersType,
  EVENT_CATEGORY_MAP,
} from 'types/timeline';
import SphereTimelineEventMolecule from '../../molecules/SphereTimelineEvent/SphereTimelineEventMolecule';
import TimelineFiltersMolecule from '../../molecules/TimelineFilters/TimelineFiltersMolecule';
import TimelineEventMarkerAtom from '../../atoms/TimelineEventMarker/TimelineEventMarkerAtom';
import './SphereTimelineOrganism.scss';

export interface SphereTimelineProps {
  sphereId: string;
  apiClients: ApiClients;
  defaultFilters?: Partial<TimelineFiltersType>;
}

const DEFAULT_LIMIT = 20;

const SphereTimelineOrganism: React.FC<SphereTimelineProps> = ({
  sphereId,
  apiClients,
  defaultFilters = {},
}) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const [filters, setFilters] = useState<TimelineFiltersType>({
    eventTypes: defaultFilters.eventTypes || [],
    categories: defaultFilters.categories || [],
    dateRange: defaultFilters.dateRange || { start: null, end: null },
  });

  const observerTarget = useRef<HTMLDivElement>(null);

  // Fetch timeline events
  const fetchTimelineEvents = useCallback(
    async (currentOffset: number, append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setError(null);
        }

        // Build query parameters
        const params: any = {
          limit: DEFAULT_LIMIT,
          offset: currentOffset,
          sortOrder: 'desc',
        };

        if (filters.eventTypes.length > 0) {
          params.eventTypes = filters.eventTypes.join(',');
        }

        if (filters.categories.length > 0) {
          // For now, send first category (API supports single category)
          params.category = filters.categories[0];
        }

        if (filters.dateRange.start) {
          params.startDate = filters.dateRange.start.toISOString();
        }

        if (filters.dateRange.end) {
          params.endDate = filters.dateRange.end.toISOString();
        }

        const response = await apiClients.graphClient.get<TimelineQueryResponse>(
          `/spheres/${sphereId}/timeline`,
          { params }
        );

        if (response.success) {
          if (append) {
            setEvents((prev) => [...prev, ...response.data]);
          } else {
            setEvents(response.data);
          }
          setHasMore(response.hasMore);
          setTotal(response.total);
        } else {
          setError(t('timeline.errors.loadFailed'));
        }
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'response' in err && err.response && typeof err.response === 'object' && 'status' in err.response && err.response.status === 403) {
          setError(t('timeline.errors.accessDenied'));
        } else {
          setError(t('timeline.errors.loadFailed'));
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [sphereId, apiClients, filters, t]
  );

  // Initial load and filter changes
  useEffect(() => {
    setOffset(0);
    fetchTimelineEvents(0, false);
  }, [filters, sphereId]);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const newOffset = offset + DEFAULT_LIMIT;
          setOffset(newOffset);
          fetchTimelineEvents(newOffset, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, offset, fetchTimelineEvents]);

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      eventTypes: [],
      categories: [],
      dateRange: { start: null, end: null },
    });
  };

  // Retry on error
  const handleRetry = () => {
    setOffset(0);
    fetchTimelineEvents(0, false);
  };

  // Render timeline content/marker for PrimeReact Timeline
  const customizedMarker = (event: TimelineEvent) => {
    const category = EVENT_CATEGORY_MAP[event.eventType];
    return <TimelineEventMarkerAtom eventType={event.eventType} category={category} size="medium" />;
  };

  const customizedContent = (event: TimelineEvent) => {
    return <SphereTimelineEventMolecule event={event} />;
  };

  // Determine responsive layout
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="sphere-timeline">
      {/* Filters */}
      <TimelineFiltersMolecule
        filters={filters}
        onFiltersChange={setFilters}
        onReset={handleResetFilters}
      />

      {/* Loading State */}
      {loading && (
        <div className="sphere-timeline__loading">
          <ProgressSpinner />
          <p>{t('timeline.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="sphere-timeline__error">
          <Message severity="error" text={error} />
          <Button
            label={t('timeline.errors.retryButton')}
            icon="pi pi-refresh"
            onClick={handleRetry}
            className="p-button-outlined"
          />
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && events.length === 0 && (
        <div className="sphere-timeline__empty">
          <i className="pi pi-calendar" />
          <p>
            {filters.eventTypes.length > 0 ||
            filters.categories.length > 0 ||
            filters.dateRange.start ||
            filters.dateRange.end
              ? t('timeline.emptyFiltered')
              : t('timeline.empty')}
          </p>
        </div>
      )}

      {/* Timeline Events */}
      {!loading && !error && events.length > 0 && (
        <>
          <Timeline
            value={events}
            layout={isMobile ? 'vertical' : 'horizontal'}
            align={isMobile ? 'left' : 'alternate'}
            marker={customizedMarker}
            content={customizedContent}
            className="sphere-timeline__timeline"
          />

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <div ref={observerTarget} className="sphere-timeline__load-trigger">
              {loadingMore && (
                <div className="sphere-timeline__loading-more">
                  <ProgressSpinner />
                  <span>{t('timeline.loadMore')}</span>
                </div>
              )}
            </div>
          )}

          {/* Event Count */}
          <div className="sphere-timeline__footer">
            <p className="sphere-timeline__count">
              {t('timeline.filters.showing')} {events.length} {t('timeline.filters.of')} {total} {t('timeline.filters.events')}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default SphereTimelineOrganism;
